import { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material'
import {
  Edit as EditIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material'
import { useMonthlyPerformance, useBookersWithTargets } from '../../hooks/api/useTargets'
import SetTargetsModal from '../../components/targets/SetTargetsModal'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getCompanyUom } from '../../utils/companyUom'
import StatusChip from '../../components/ui/StatusChip'
import { formatQuantity } from '../../utils/formatters'

const TargetsPage = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const uom = getCompanyUom(user?.company_uom)
  const currentDate = new Date()
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear())
  const [modalOpen, setModalOpen] = useState(false)

  const { data: performanceData, isLoading, error } = useMonthlyPerformance(selectedMonth, selectedYear)
  const { data: bookersData } = useBookersWithTargets()

  const bookers = performanceData?.data?.bookers || []
  const totals = performanceData?.data?.totals
  const totalDays = performanceData?.data?.total_days || 31
  const today = currentDate.getDate()

  const getMonthName = (month: number) => {
    return new Date(2000, month - 1, 1).toLocaleString('default', { month: 'long' })
  }

  const getProgressVariant = (pct: number): 'success' | 'warn' | 'danger' => {
    if (pct >= 80) return 'success'
    if (pct >= 50) return 'warn'
    return 'danger'
  }

  const getProgressBarColor = (pct: number) => {
    if (pct >= 80) return '#2d6a4f'
    if (pct >= 50) return '#7a5a20'
    return '#8a3020'
  }

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) { setSelectedMonth(12); setSelectedYear(selectedYear - 1) }
    else setSelectedMonth(selectedMonth - 1)
  }

  const handleNextMonth = () => {
    if (selectedMonth === 12) { setSelectedMonth(1); setSelectedYear(selectedYear + 1) }
    else setSelectedMonth(selectedMonth + 1)
  }

  const isCurrentMonth = selectedMonth === currentDate.getMonth() + 1 && selectedYear === currentDate.getFullYear()

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">Failed to load performance data. Please try again later.</Alert>
      </Box>
    )
  }

  const dayCellSx = (dayNum: number) => ({
    px: 1,
    ...(isCurrentMonth && dayNum === today ? { bgcolor: '#e8f5ee' } : {}),
  })

  return (
    <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ mb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Bookers Performance
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <IconButton onClick={handlePreviousMonth} size="small" sx={{ color: '#7a766f' }}>
              <ChevronLeftIcon />
            </IconButton>
            <Box sx={{ px: 1.5, py: 0.5, border: '1px solid #e8e4dd', borderRadius: 1, bgcolor: '#f7f5f2' }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#2c2a26' }}>
                {getMonthName(selectedMonth)} {selectedYear}
              </Typography>
            </Box>
            <IconButton onClick={handleNextMonth} size="small" sx={{ color: '#7a766f' }}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setModalOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          Set Targets
        </Button>
      </Box>

      {/* Summary Cards */}
      {totals && (
        <Box sx={{ mb: 2.5, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <Paper sx={{ p: 2 }}>
            <Typography className="caps muted" gutterBottom>Month Progress</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
              <Typography variant="h5" fontWeight={700}>Day {today}</Typography>
              <Typography variant="body2" color="text.secondary">of {totalDays}</Typography>
            </Box>
            <Box sx={{ width: '100%', height: 6, bgcolor: '#e8e4dd', borderRadius: 999, overflow: 'hidden' }}>
              <Box sx={{ width: `${(today / totalDays) * 100}%`, height: '100%', bgcolor: '#2d6a4f', borderRadius: 999 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Typography variant="caption" color="text.secondary">{today} gone</Typography>
              <Typography variant="caption" color="text.secondary">{totalDays - today} remaining</Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography className="caps muted" gutterBottom>Time Elapsed</Typography>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {Math.round((today / totalDays) * 100)}%
            </Typography>
            <Box sx={{ width: '100%', height: 6, bgcolor: '#e8e4dd', borderRadius: 999, overflow: 'hidden' }}>
              <Box sx={{ width: `${(today / totalDays) * 100}%`, height: '100%', bgcolor: '#2d6a4f', borderRadius: 999 }} />
            </Box>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Total {uom.code} Progress
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                {formatQuantity(totals.achieved_ctn)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                / {formatQuantity(totals.target_ctn)}
              </Typography>
            </Box>
            <Box sx={{ width: '100%', height: 6, bgcolor: '#e8e4dd', borderRadius: 999, overflow: 'hidden' }}>
              <Box sx={{ width: `${Math.min(totals.progress_percentage_ctn, 100)}%`, height: '100%', bgcolor: getProgressBarColor(totals.progress_percentage_ctn), borderRadius: 999 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {totals.progress_percentage_ctn}% Complete
            </Typography>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography className="caps muted" gutterBottom>Total Bills Progress</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
              <Typography variant="h5" fontWeight={700}>{totals.achieved_bills.toLocaleString()}</Typography>
              <Typography variant="body2" color="text.secondary">/ {totals.target_bills.toLocaleString()}</Typography>
            </Box>
            <Box sx={{ width: '100%', height: 6, bgcolor: '#e8e4dd', borderRadius: 999, overflow: 'hidden' }}>
              <Box sx={{ width: `${Math.min(totals.progress_percentage_bills, 100)}%`, height: '100%', bgcolor: getProgressBarColor(totals.progress_percentage_bills), borderRadius: 999 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {totals.progress_percentage_bills}% Complete
            </Typography>
          </Paper>
        </Box>
      )}

      {/* CTN Performance Table */}
      <Paper sx={{ mb: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e8e4dd', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: '#2c2a26' }}>{uom.code} Performance</Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 400, overflowX: 'auto' }}>
          <Table stickyHeader size="small" sx={{ tableLayout: 'auto' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 120, position: 'sticky', left: 0, bgcolor: '#f7f5f2', zIndex: 2, px: 1 }}>Booker</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: 70, px: 1 }}>Target</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: 70, px: 1 }}>Ach</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: 70, px: 1 }}>Rem</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, width: 60, px: 1 }}>%</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: 90, px: 1, whiteSpace: 'nowrap' }}>Sales (Rs.)</TableCell>
                {Array.from({ length: totalDays }, (_, i) => (
                  <TableCell
                    key={i}
                    align="right"
                    sx={{
                      fontWeight: 600,
                      width: 50,
                      px: 1,
                      ...(isCurrentMonth && i + 1 === today ? { bgcolor: '#e8f5ee' } : { bgcolor: '#f7f5f2' }),
                    }}
                  >
                    D{i + 1}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {bookers.map((booker) => (
                <TableRow key={booker.id} hover>
                  <TableCell sx={{ position: 'sticky', left: 0, bgcolor: '#ffffff', zIndex: 1, px: 1 }}>
                    {booker.name}
                  </TableCell>
                  <TableCell align="right" sx={{ px: 1 }} className="tnum">{formatQuantity(booker.target_ctn)}</TableCell>
                  <TableCell align="right" sx={{ px: 1 }} className="tnum">{formatQuantity(booker.achieved_ctn)}</TableCell>
                  <TableCell align="right" sx={{ px: 1 }} className="tnum">{formatQuantity(booker.remaining_ctn)}</TableCell>
                  <TableCell align="center" sx={{ px: 1 }}>
                    <StatusChip label={`${booker.progress_percentage_ctn}%`} variant={getProgressVariant(booker.progress_percentage_ctn)} />
                  </TableCell>
                  <TableCell align="right" sx={{ px: 1 }} className="tnum">
                    {Math.round(booker.achieved_sales_value ?? 0).toLocaleString()}
                  </TableCell>
                  {booker.daily_breakdown.map((day) => (
                    <TableCell key={day.day} align="right" sx={dayCellSx(day.day)} className="tnum">
                      {day.ctn > 0 ? formatQuantity(day.ctn) : <span className="faint">—</span>}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {totals && (
                <TableRow sx={{ bgcolor: '#f7f5f2' }}>
                  <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: '#f7f5f2', zIndex: 1, px: 1 }}>TOTAL</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, px: 1 }} className="tnum">{formatQuantity(totals.target_ctn)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, px: 1 }} className="tnum">{formatQuantity(totals.achieved_ctn)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, px: 1 }} className="tnum">{formatQuantity(totals.remaining_ctn)}</TableCell>
                  <TableCell align="center" sx={{ px: 1 }}>
                    <StatusChip label={`${totals.progress_percentage_ctn}%`} variant={getProgressVariant(totals.progress_percentage_ctn)} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, px: 1 }} className="tnum">
                    {Math.round(totals.achieved_sales_value ?? 0).toLocaleString()}
                  </TableCell>
                  {totals.daily_breakdown.map((day) => (
                    <TableCell key={day.day} align="right" sx={{ fontWeight: 600, ...dayCellSx(day.day) }} className="tnum">
                      {day.ctn > 0 ? formatQuantity(day.ctn) : <span className="faint">—</span>}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Bills Performance Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e8e4dd', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: '#2c2a26' }}>Bills Performance</Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 350, overflowX: 'auto' }}>
          <Table stickyHeader size="small" sx={{ tableLayout: 'auto' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 120, position: 'sticky', left: 0, bgcolor: '#f7f5f2', zIndex: 2, px: 1, py: 1 }}>Booker</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: 70, px: 1, py: 1 }}>Target</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: 70, px: 1, py: 1 }}>Ach</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: 70, px: 1, py: 1 }}>Rem</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, width: 60, px: 1, py: 1 }}>%</TableCell>
                {Array.from({ length: totalDays }, (_, i) => (
                  <TableCell
                    key={i}
                    align="right"
                    sx={{
                      fontWeight: 600,
                      width: 50,
                      px: 1,
                      py: 1,
                      ...(isCurrentMonth && i + 1 === today ? { bgcolor: '#e8f5ee' } : { bgcolor: '#f7f5f2' }),
                    }}
                  >
                    D{i + 1}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {bookers.map((booker) => (
                <TableRow key={booker.id} hover>
                  <TableCell sx={{ position: 'sticky', left: 0, bgcolor: '#ffffff', zIndex: 1, px: 1, py: 0.75 }}>
                    {booker.name}
                  </TableCell>
                  <TableCell align="right" sx={{ px: 1, py: 0.75 }} className="tnum">{booker.target_bills.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ px: 1, py: 0.75 }} className="tnum">{booker.achieved_bills.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ px: 1, py: 0.75 }} className="tnum">{booker.remaining_bills.toLocaleString()}</TableCell>
                  <TableCell align="center" sx={{ px: 1, py: 0.75 }}>
                    <StatusChip label={`${booker.progress_percentage_bills}%`} variant={getProgressVariant(booker.progress_percentage_bills)} />
                  </TableCell>
                  {booker.daily_breakdown.map((day) => (
                    <TableCell key={day.day} align="right" sx={{ py: 0.75, ...dayCellSx(day.day) }} className="tnum">
                      {day.bills > 0 ? day.bills : <span className="faint">—</span>}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {totals && (
                <TableRow sx={{ bgcolor: '#f7f5f2' }}>
                  <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: '#f7f5f2', zIndex: 1, px: 1, py: 0.75 }}>TOTAL</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, px: 1, py: 0.75 }} className="tnum">{totals.target_bills.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, px: 1, py: 0.75 }} className="tnum">{totals.achieved_bills.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, px: 1, py: 0.75 }} className="tnum">{totals.remaining_bills.toLocaleString()}</TableCell>
                  <TableCell align="center" sx={{ px: 1, py: 0.75 }}>
                    <StatusChip label={`${totals.progress_percentage_bills}%`} variant={getProgressVariant(totals.progress_percentage_bills)} />
                  </TableCell>
                  {totals.daily_breakdown.map((day) => (
                    <TableCell key={day.day} align="right" sx={{ fontWeight: 600, py: 0.75, ...dayCellSx(day.day) }} className="tnum">
                      {day.bills > 0 ? day.bills : <span className="faint">—</span>}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {bookersData && (
        <SetTargetsModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          bookers={bookersData.data.bookers}
          month={selectedMonth}
          year={selectedYear}
        />
      )}
    </Box>
  )
}

export default TargetsPage
